import { RemoveCircleOutlined, AddCircleOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Paper, Popper, Slider, Stack, Typography } from '@mui/material';
import { CustomCellEditorProps } from "ag-grid-react";
import React, { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Mark } from "../../../common/models/Mark";

export type IFeetAndInchesCellEditorProps = {
    maxFeet: number,
    label: string;
    showDelete?: boolean;
} & CustomCellEditorProps;

export function FeetAndInchesCellEditor({
    maxFeet,
    label,
    showDelete,
    value,
    eGridCell,
    api,
    onValueChange
}: IFeetAndInchesCellEditorProps): JSX.Element {

    const createInitialState = () => {
        let feet: number = 0;
        let inches: number = 0;

        if (value) {
            const arr: string[] = value.split(" ");

            if (arr.length === 2) {
                feet = parseFloat(arr[0].replace(/'/g, ""));
                inches = parseFloat(arr[1].replace(/"/g, ""));
            }
        }

        return {
            //value: value,
            feet,
            inches
        };
    };
    const initialState = createInitialState();
    // const theme = useTheme();
    // const [value, setValue] = useState<string>(initialState.value);
    const [feet, setFeet] = useState<number | undefined>(initialState.feet);
    const [inches, setInches] = useState<number | undefined>(initialState.inches);
    const { t } = useTranslation();

    // let deleted = false;

    const onValueChanged = (name: string, value: number) => {

        if (name === 'feet') {

            const newFeet = value !== undefined ? value : inches !== undefined ? 0 : undefined;
            const newInches = inches !== undefined ? inches : newFeet !== undefined ? 0 : undefined;

            setFeet(newFeet);

            if (newFeet === undefined && newInches === undefined) {
                // setValue('');
                onValueChange('');

            } else {
                // setValue(`${newFeet?.toFixed(0) ?? ''}' ${newInches?.toFixed(2)}"`);
                onValueChange(`${newFeet?.toFixed(0) ?? ''}' ${newInches?.toFixed(2)}"`);
            }

        } else if (name === 'inches') {

            const newInches = value !== undefined ? value : feet !== undefined ? 0 : undefined;
            const newFeet = feet !== undefined ? feet : newInches !== undefined ? 0 : undefined;

            setInches(newInches);

            if (newFeet === undefined && newInches === undefined) {
                // setValue('');
                onValueChange('');

            } else {
                // setValue(`${newFeet?.toFixed(0) ?? ''}' ${newInches?.toFixed(2)}"`);
                onValueChange(`${newFeet?.toFixed(0) ?? ''}' ${newInches?.toFixed(2)}"`);
            }
        }
    };

    // /* ag-grid component Editor Lifecycle methods */
    // useImperativeHandle(ref, () => {
    //     return {
    //         // the final value to send to the grid, on completion of editing
    //         getValue() {
    //             if (deleted) {
    //                 deleted = false;
    //                 return '';
    //             }
    //             return value;
    //         },

    //         isPopup() {
    //             return true;
    //         },

    //         // Gets called once before editing starts, to give editor a chance to
    //         // cancel the editing before it even starts.
    //         isCancelBeforeStart() {
    //             //return cancelBeforeStart;
    //             return false;
    //         },

    //         // Gets called once when editing is finished (eg if enter is pressed).
    //         // If you return true, then the result of the edit will be ignored.
    //         isCancelAfterEnd() {
    //             // will reject the number if it greater than 1,000,000
    //             // not very practical, but demonstrates the method.
    //             //return value > 1000000;
    //             return false;
    //         }
    //     };
    // });    

    const feetList: Mark[] = [];

    const increment = 4 + ((maxFeet % 4) ? 1 : 0);
    for (let i: number = 0; i <= maxFeet; i += increment) {
        feetList.push({
            label: `${i}'`,
            value: i
        });
    }

    const inchesList: Mark[] = [];
    for (let i: number = 0; i <= 12; i += 3) {
        inchesList.push({
            label: `${i.toFixed(2)}"`,
            value: i
        });
    }

    function getFeetValuetext(value: number) {
        return `${value}'`;
    }

    function getInchesValuetext(value: number) {
        return `${value.toFixed(2)}"`;
    }

    function onDeleteValue(/*ev: React.MouseEvent<HTMLButtonElement>*/) {

        // clear the state value, however, since this will close the cell editor the deleted
        // variable will be set so the getValue lifecycle function can return the cleared
        // value. Otherwise, the cell editor does not have a change to rerender based on the 
        // state change and the value will remain unchanged.
        // setValue('');
        onValueChange('');
        // deleted = true;
        api.tabToNextCell();
    }

    return (
        <Popper
            open={true}
            anchorEl={eGridCell}
            placement="bottom-start"
        >
            <Paper
                sx={{
                    width: 320,
                    padding: theme => theme.spacing(3)
                }}
                variant="elevation"
                elevation={8}
            >
                <Stack direction='row'>

                    <Typography variant='subtitle1' gutterBottom sx={{ marginTop: theme => theme.spacing(.25) }}>{label}</Typography>

                    {
                        showDelete && <Button
                            sx={{
                                marginLeft: "auto"
                            }}
                            onClick={onDeleteValue}
                        >
                            {t("buttons.delete")}
                        </Button>
                    }
                </Stack>


                <Box>
                    <Typography id="feet-label" gutterBottom variant='subtitle2'>
                        {`Feet: ${feet !== undefined ? getFeetValuetext(feet) : ''}`}
                    </Typography>

                    <Stack direction='row' sx={{
                        justifyContent: 'space-between'
                    }}>
                        <IconButton
                            sx={{
                                fontSize: '1rem',
                                marginTop: 0,
                                paddingTop: '4px',
                                marginBottom: 'auto',
                                marginRight: theme => theme.spacing(2)
                            }}
                            onClick={() => {
                                if (feet! > 0) {
                                    const newFeet = feet! - 1;
                                    onValueChanged('feet', newFeet);
                                }
                            }}
                        >
                            <RemoveCircleOutlined />
                        </IconButton>

                        <Slider
                            key='feetSlider'
                            name='feet'
                            defaultValue={initialState.feet}
                            value={feet}
                            getAriaValueText={getFeetValuetext}
                            aria-labelledby="feet-label"
                            step={1}
                            marks={feetList}
                            valueLabelDisplay="off"
                            min={0}
                            max={maxFeet}
                            onChange={(event: Event, value: number | number[]) => { onValueChanged('feet', value as number); }}
                        />

                        <IconButton
                            sx={{
                                fontSize: '1rem',
                                marginTop: 0,
                                paddingTop: '4px',
                                marginBottom: 'auto',
                                marginLeft: theme => theme.spacing(2)
                            }}
                            onClick={() => {
                                if (feet! < maxFeet) {
                                    const newFeet = feet! + 1;
                                    onValueChanged('feet', newFeet);
                                }

                            }}
                        >
                            <AddCircleOutlined />
                        </IconButton>
                    </Stack>
                </Box>

                <Box>
                    <Typography id="inches-label" gutterBottom variant='subtitle2'>
                        {`Inches: ${inches !== undefined ? getInchesValuetext(inches) : ''}`}
                    </Typography>

                    <Stack direction='row' sx={{
                        justifyContent: 'space-between'
                    }}>
                        <IconButton
                            sx={{
                                fontSize: '1rem',
                                marginTop: 0,
                                paddingTop: '4px',
                                marginBottom: 'auto',
                                marginRight: theme => theme.spacing(2)

                            }}
                            onClick={() => {
                                if (inches! > 0) {
                                    const newInches = inches! - .25;
                                    onValueChanged('inches', newInches);
                                }
                            }}
                        >
                            <RemoveCircleOutlined />
                        </IconButton>

                        <Slider
                            key='inchesSlider'
                            defaultValue={initialState.inches}
                            value={inches}
                            getAriaValueText={getInchesValuetext}
                            aria-labelledby="inches-label"
                            step={.25}
                            marks={inchesList}
                            valueLabelDisplay="off"
                            min={0}
                            max={11.75}
                            onChange={(event: Event, value: number | number[]) => { onValueChanged('inches', value as number); }}
                        />

                        <IconButton
                            sx={{
                                fontSize: '1rem',
                                marginTop: 0,
                                paddingTop: '4px',
                                marginBottom: 'auto',
                                marginLeft: theme => theme.spacing(2)
                            }}
                            onClick={() => {
                                if (inches! < 11.75) {
                                    const newInches = inches! + .25;
                                    onValueChanged('inches', newInches);
                                }
                            }}
                        >
                            <AddCircleOutlined />
                        </IconButton>
                    </Stack>
                </Box>
            </Paper>
        </Popper>
    );
}